/**
 * The shared chart-lightbox store (launch round, 2026-10-07: "for all charts in the system,
 * we need to make them expandable").
 *
 * The same shape as `chartTipStore`: one module-level slot that every `ChartFrame` writes
 * into and the single `ChartLightbox` node (mounted once, beside `ChartTip`) reads. One slot
 * is the point: two frames can never open two dialogs, because opening a second chart
 * replaces the first.
 *
 * What is stored is the frame's own **render-prop**, not a snapshot of the chart: the
 * lightbox calls it again at its own size, so the expanded chart is the same component fed
 * the same data the page already holds. Nothing is refetched and nothing is computed until
 * the dialog is open.
 */
import type { ReactNode } from 'react';

/** What a frame hands its chart: the size to draw at, and whether this is the lightbox. */
export interface ChartSize {
  w: number;
  h: number;
  expanded: boolean;
}

export interface ChartLightboxState {
  /** The owning frame's id; only that frame may update or close the open dialog. */
  key: string;
  title: string;
  subtitle?: string;
  legend?: ReactNode;
  xLabel?: string;
  yLabel?: string;
  render: (size: ChartSize) => ReactNode;
  /** The expand button that opened the dialog, which gets focus back when it closes. */
  opener: HTMLElement | null;
}

/** The lightbox's chart area: `min(1400, viewport − 96)` by `min(820, viewport − 220)` px. */
export const LIGHTBOX_MAX_W = 1400;
export const LIGHTBOX_MAX_H = 820;
export const LIGHTBOX_SIDE = 96;
export const LIGHTBOX_CHROME = 220;
/** Never smaller than this, so a phone held sideways still gets a chart. */
export const LIGHTBOX_MIN = 160;
/**
 * How much larger than its viewBox a chart is drawn in the lightbox. A chart that takes a
 * width is laid out at `size.w / LIGHTBOX_ZOOM` and scaled back up, so its 8-9 px text reads
 * at 13-14 px rather than staying 8 px across 1,400 px of screen.
 */
export const LIGHTBOX_ZOOM = 1.6;
/** The most a fixed-width chart (one with no `w` prop) is scaled up in the lightbox. */
export const LIGHTBOX_MAX_ZOOM = 2.4;

/** The lightbox chart area for a viewport of `vw` × `vh` px. */
/** At or below this viewport width the dialog's CSS takes its narrow-screen padding. */
export const LIGHTBOX_NARROW = 520;
/** The dialog's horizontal chrome on a narrow screen: 2 × 8 px backdrop + 2 × 12 px body. */
export const LIGHTBOX_NARROW_SIDE = 40;
/** The narrowest chart area the lightbox lays a chart out at. */
export const LIGHTBOX_MIN_W = 320;

export function lightboxSize(vw: number, vh: number): { w: number; h: number } {
  /*
    On a narrow screen the chart takes the dialog's whole content width (the dialog's own
    padding is what is left of the viewport), never less than 320 (launch round follow-up).
  */
  const w = vw <= LIGHTBOX_NARROW ? vw - LIGHTBOX_NARROW_SIDE : Math.min(LIGHTBOX_MAX_W, vw - LIGHTBOX_SIDE);
  return {
    w: Math.max(LIGHTBOX_MIN_W, w),
    h: Math.max(LIGHTBOX_MIN, Math.min(LIGHTBOX_MAX_H, vh - LIGHTBOX_CHROME)),
  };
}

/** A chart's `w` prop: its inline width on the page, the lightbox width when expanded. */
export function chartW(size: ChartSize, inline: number): number {
  return size.expanded ? Math.max(inline, Math.round(size.w / LIGHTBOX_ZOOM)) : inline;
}

/**
 * Room kept under a chart in the lightbox for what it draws outside its SVG (the targets
 * chart's caption row, a legend list), so the chart and its captions fit the area together.
 */
export const LIGHTBOX_RESERVE = 60;

/** A chart's `h` prop, on the same terms as `chartW`, less `LIGHTBOX_RESERVE`. */
export function chartH(size: ChartSize, inline: number): number {
  return size.expanded
    ? Math.max(inline, Math.round(Math.max(0, size.h - LIGHTBOX_RESERVE) / LIGHTBOX_ZOOM))
    : inline;
}

let state: ChartLightboxState | null = null;
const listeners = new Set<() => void>();

function emit(next: ChartLightboxState | null): void {
  state = next;
  for (const listener of listeners) listener();
}

export function subscribeChartLightbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The open chart, or `null`. Also the test/reset read. */
export const readChartLightbox = (): ChartLightboxState | null => state;

/** Open (or replace) the one lightbox. */
export function openChartLightbox(next: ChartLightboxState): void {
  emit(next);
}

/** Refresh the open dialog's content, if `key` still owns it — the page re-rendered. */
export function updateChartLightbox(key: string, patch: Partial<Omit<ChartLightboxState, 'key'>>): void {
  if (!state || state.key !== key) return;
  emit({ ...state, ...patch });
}

/**
 * Close the lightbox and give focus back to the button that opened it.
 *
 * `restoreFocus` is false when the owning frame is unmounting: its button is leaving the
 * page with it, and focusing a detached node would drop focus on `<body>` anyway.
 */
export function closeChartLightbox(restoreFocus = true): void {
  if (!state) return;
  const opener = state.opener;
  emit(null);
  if (restoreFocus && opener && opener.isConnected) opener.focus();
}
